import { UserListResponse } from 'api/actions/user-list/user-list-response';
import { useApi } from 'api/api-context';
import {
  DataSelectInheritedProps,
  DataSelect,
  DataMultiSelectInheritedProps,
  DataMultiSelect,
} from 'components/selects/DataSelect';
import { nanoid } from 'nanoid';
import { useCallback, useMemo } from 'react';

type PermissionSlug = keyof UserListResponse[number]['permissions']['anywhere'];

/**
 * Fetches users from API.
 */
function useUserSelect({
  showDiscarded,
  organizationId,
  hasPermissionAnywhere,
}: {
  showDiscarded: boolean;
  organizationId?: number;
  hasPermissionAnywhere?: PermissionSlug;
}) {
  const { getAction } = useApi();

  const fetchUsers = useCallback(async () => {
    const action = getAction('UserList');

    const users = await action({
      query: {
        filters: {
          'organizationId.eq': organizationId === -1 ? undefined : organizationId,
          'showDiscarded.eq': showDiscarded ? 1 : 0,
        },
      },
    });

    return users
      .filter((user) => !hasPermissionAnywhere || user.permissions.anywhere[hasPermissionAnywhere])
      .map((user) => ({
        label: (user.status ? '' : '[VYRADENÝ] ') + user.fullName,
        value: user.userId,
        group: user.organization.organizationName,
      }));
  }, [getAction, organizationId, hasPermissionAnywhere, showDiscarded]);

  return { fetchUsers };
}

interface UserSelectProp {
  showDiscarded?: boolean;
  organizationId?: number;
  hasPermissionAnywhere?: PermissionSlug;
}

type UserSelectProps = DataSelectInheritedProps & UserSelectProp;
type UserMultiSelectProps = DataMultiSelectInheritedProps & UserSelectProp;

/**
 * Select input for users.
 */
export function UserSelect({ showDiscarded = true, organizationId, hasPermissionAnywhere, ...props }: UserSelectProps) {
  const key = useMemo(() => nanoid(), [organizationId, hasPermissionAnywhere]);
  const { fetchUsers } = useUserSelect({ showDiscarded, organizationId, hasPermissionAnywhere });

  return <DataSelect key={key} action={fetchUsers} labelProp="label" valueProp="value" groupProp="group" {...props} />;
}

/**
 * Select input for users (multiple).
 */
export function UserMultiSelect({
  showDiscarded = true,
  organizationId,
  hasPermissionAnywhere,
  ...props
}: UserMultiSelectProps) {
  const key = useMemo(() => nanoid(), [organizationId, hasPermissionAnywhere]);
  const { fetchUsers } = useUserSelect({ showDiscarded, organizationId, hasPermissionAnywhere });

  return (
    <DataMultiSelect key={key} action={fetchUsers} labelProp="label" valueProp="value" groupProp="group" {...props} />
  );
}
